/* Shared helpers for the sample designs: sizes, money, cart and the product form. */
(function () {
  const TT = (window.TT = window.TT || {});

  TT.FREE_SHIP_CENTS = 24900;

  TT.money = function (cents) {
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  /** "7 ft 6 in" -> 7.5, "16" -> 16, "20 ft" -> 20 */
  TT.parseLen = function (text) {
    const s = String(text).toLowerCase().trim();
    const ft = s.match(/(\d+(?:\.\d+)?)\s*(?:ft|'|′)/);
    const inch = s.match(/(\d+(?:\.\d+)?)\s*(?:in\b|"|″)/);
    if (ft || inch) return (ft ? parseFloat(ft[1]) : 0) + (inch ? parseFloat(inch[1]) / 12 : 0);
    const n = parseFloat(s);
    return isNaN(n) ? null : n;
  };

  /** "16 x 20 ft" -> {w: 16, l: 20, a: 16, b: 20, sqft: 320} (w is always the short side) */
  TT.parseSize = function (title) {
    const parts = String(title).split(/\s+[x×]\s+/i);
    if (parts.length !== 2) return null;
    const a = TT.parseLen(parts[0]);
    const b = TT.parseLen(parts[1]);
    if (a == null || b == null) return null;
    return { a: a, b: b, w: Math.min(a, b), l: Math.max(a, b), sqft: Math.round(a * b) };
  };

  /** 7.5 -> "7′6″", 20 -> "20′" */
  TT.fmtLen = function (ft) {
    const whole = Math.floor(ft + 1e-9);
    const inch = Math.round((ft - whole) * 12);
    return inch ? whole + '′' + inch + '″' : whole + '′';
  };

  TT.lb = function (grams) {
    return Math.round(grams / 453.592);
  };

  TT.hasTag = function (product, tag) {
    return (product.tags || []).indexOf(tag) !== -1;
  };

  let catalogCache = null;
  TT.catalog = function () {
    if (catalogCache) return catalogCache;
    const el = document.querySelector('[data-tt-catalog]');
    catalogCache = el ? JSON.parse(el.textContent) : [];
    catalogCache.forEach(function (p) {
      p.variants.forEach(function (v) {
        v.size = TT.parseSize(v.title);
        v.product = p;
      });
    });
    return catalogCache;
  };

  /** Smallest available variant of a product that covers needW x needL (either orientation). */
  TT.smallestFit = function (product, needW, needL) {
    const w = Math.min(needW, needL);
    const l = Math.max(needW, needL);
    let best = null;
    product.variants.forEach(function (v) {
      if (!v.size || !v.available) return;
      if (v.size.w + 1e-9 >= w && v.size.l + 1e-9 >= l) {
        if (!best || v.size.sqft < best.size.sqft) best = v;
      }
    });
    return best;
  };

  TT.addToCart = function (items, button) {
    if (button) {
      button.disabled = true;
      button.dataset.label = button.dataset.label || button.textContent;
      button.textContent = 'Adding…';
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) throw new Error(body.description || body.message || 'Could not add to cart');
          window.location.href = '/cart';
        });
      })
      .catch(function (err) {
        if (button) {
          button.disabled = false;
          button.textContent = button.dataset.label;
        }
        window.alert(err.message);
      });
  };

  TT.freeShipMessage = function (eligible, totalCents) {
    if (!eligible) return 'Add-on item: not eligible for free shipping. Shipping is calculated at checkout.';
    if (totalCents >= TT.FREE_SHIP_CENTS) return 'This order ships free to the lower 48.';
    return 'Add ' + TT.money(TT.FREE_SHIP_CENTS - totalCents) + ' more for free shipping to the lower 48.';
  };

  /**
   * Wires a product section: variant inputs, price, quantity, free-shipping note and sticky bar.
   * Markup contract (all optional except the variants JSON):
   *   [data-tt-variants]         JSON array of {id, title, price, available, grams}
   *   [data-tt-variant-input]    radios or a select whose value is a variant id
   *   input[name="id"]           hidden id inputs inside forms
   *   [data-tt-price], [data-tt-variant-title], [data-tt-sqft], [data-tt-weight], [data-tt-ppsf]
   *   [data-tt-atc]              add-to-cart buttons
   *   [data-tt-qty], [data-tt-qty-step]
   *   [data-tt-freeship]         root[data-free-ship="true|false"]
   *   [data-tt-sticky]           bar shown once [data-tt-sticky-trigger] scrolls out of view
   */
  TT.productForm = function (root, onChange) {
    const variants = JSON.parse(root.querySelector('[data-tt-variants]').textContent);
    variants.forEach(function (v) {
      v.size = TT.parseSize(v.title);
    });
    const inputs = root.querySelectorAll('[data-tt-variant-input]');
    const qty = root.querySelector('[data-tt-qty]');
    const eligible = root.dataset.freeShip === 'true';
    let current = null;

    function setAll(selector, fn) {
      root.querySelectorAll(selector).forEach(fn);
    }

    function quantity() {
      return Math.max(1, parseInt(qty && qty.value, 10) || 1);
    }

    function render() {
      if (!current) return;
      const total = current.price * quantity();
      setAll('input[name="id"]', function (el) {
        el.value = current.id;
      });
      setAll('[data-tt-price]', function (el) {
        el.textContent = TT.money(current.price);
      });
      setAll('[data-tt-total]', function (el) {
        el.textContent = TT.money(total);
      });
      setAll('[data-tt-variant-title]', function (el) {
        el.textContent = current.title;
      });
      setAll('[data-tt-sqft]', function (el) {
        el.textContent = current.size ? current.size.sqft + ' sq ft' : '';
      });
      setAll('[data-tt-weight]', function (el) {
        el.textContent = current.grams ? '~' + TT.lb(current.grams) + ' lb' : '';
      });
      setAll('[data-tt-ppsf]', function (el) {
        el.textContent = current.size ? TT.money(Math.round(current.price / current.size.sqft)) + ' / sq ft' : '';
      });
      setAll('[data-tt-atc]', function (el) {
        el.disabled = !current.available;
        const label = el.querySelector('[data-tt-atc-label]') || el;
        label.textContent = current.available ? el.dataset.addLabel || 'Add to cart' : 'Sold out';
      });
      setAll('[data-tt-freeship]', function (el) {
        el.textContent = TT.freeShipMessage(eligible, total);
      });
      setAll('[data-tt-variant-input]', function (el) {
        if (el.type === 'radio') el.checked = String(el.value) === String(current.id);
        else el.value = current.id;
      });
      if (onChange) onChange(current, quantity());
    }

    function select(id, updateUrl) {
      const found = variants.find(function (v) {
        return String(v.id) === String(id);
      });
      if (!found) return;
      current = found;
      render();
      if (updateUrl && root.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', found.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    inputs.forEach(function (el) {
      el.addEventListener('change', function () {
        select(el.value, true);
      });
    });

    if (qty) {
      qty.addEventListener('input', render);
      setAll('[data-tt-qty-step]', function (btn) {
        btn.addEventListener('click', function () {
          qty.value = Math.max(1, quantity() + parseInt(btn.dataset.ttQtyStep, 10));
          render();
        });
      });
    }

    const sticky = root.querySelector('[data-tt-sticky]');
    const trigger = root.querySelector('[data-tt-sticky-trigger]');
    if (sticky && trigger && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        const show = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        // The bar is display:none on desktop; only move the switcher pill when it's really there.
        document.body.classList.toggle('tt-has-sticky-bar', show && getComputedStyle(sticky).display !== 'none');
      }).observe(trigger);
    }

    select(root.dataset.selectedVariant || variants[0].id, false);
    return {
      select: function (id) {
        select(id, true);
      },
      current: function () {
        return current;
      },
      quantity: quantity,
      variants: variants,
    };
  };

  /** Small detail windows: [data-tt-dlg-open="id"] opens <dialog id>; [data-tt-dlg-close] or a click outside closes it;
   *  [data-tt-dlg-check="variantId"] ticks the matching add-on checkbox and closes. */
  document.addEventListener('click', function (event) {
    const opener = event.target.closest('[data-tt-dlg-open]');
    if (opener) {
      const dlg = document.getElementById(opener.dataset.ttDlgOpen);
      if (dlg && typeof dlg.showModal === 'function') {
        event.preventDefault();
        dlg.showModal();
      }
      return;
    }
    const dlg = event.target.closest('dialog.ttA-dlg');
    if (!dlg) return;
    const check = event.target.closest('[data-tt-dlg-check]');
    if (check) {
      const root = dlg.closest('form, [data-tt-product]') || document;
      const box = root.querySelector('[data-tt-addon][value="' + check.dataset.ttDlgCheck + '"]');
      if (box && !box.checked) {
        box.checked = true;
        box.dispatchEvent(new Event('change', { bubbles: true }));
      }
      dlg.close();
      return;
    }
    if (event.target.closest('[data-tt-dlg-close]') || event.target === dlg) dlg.close();
  });

  /** Simple accordion: [data-tt-accordion] > button[aria-controls] */
  document.addEventListener('click', function (event) {
    const toggle = event.target.closest('[data-tt-accordion-toggle]');
    if (!toggle) return;
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    const open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!open));
    if (panel) panel.hidden = open;
  });

  /** Image galleries: [data-tt-gallery] with [data-tt-thumb] buttons and a [data-tt-main] img */
  document.addEventListener('click', function (event) {
    const thumb = event.target.closest('[data-tt-thumb]');
    if (!thumb) return;
    const gallery = thumb.closest('[data-tt-gallery]');
    const main = gallery && gallery.querySelector('[data-tt-main]');
    if (!main) return;
    main.src = thumb.dataset.src;
    main.srcset = thumb.dataset.srcset || '';
    main.alt = thumb.dataset.alt || '';
    gallery.querySelectorAll('[data-tt-thumb]').forEach(function (t) {
      t.setAttribute('aria-current', String(t === thumb));
    });
  });
})();
